import { compose } from 'recompose';
import { ContextProvider, Context } from './store';
import { Grid, IconButton } from '@material-ui/core';
import { css } from '@emotion/react';
import Snackbar from '@/components/Snackbar';
import React, { useContext, useState, useMemo } from 'react';
import { colors } from '@/styles';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import Tooltip from '@mui/material/Tooltip';
import { OnwardTab, OnwardTabContainer } from '@/components/Tabs';
import { CLAIM_DETAILS_MODALS, TABS } from './constants';
import { OnwardBreadcrumbActive, OnwardBreadcrumbInactive, OnwardBreadcrumbSpacer } from '@/components/Breadcrumbs';
import { useNavigate } from 'react-router';
import { CircularProgress } from '@mui/material';
import { PrimaryButton } from '@/styles/blocks';
import EditClaimModal from '../modals/EditClaimModal';
import CloseClaimModal from '../modals/CloseClaimModal';
import { Title, Status } from '../blocks';
import { CLAIM_STATUS_COLORS, CLAIM_STATUSES } from '../constants';
import ClaimInfoCard from './cards/ClaimInfoCard';
import ClaimAdjustmentsCard from './cards/ClaimAdjustmentsCard';
import ClaimItemsCard from './cards/ClaimItemsCard';
import SupportingDocumentsCard from './cards/SupportingDocumentsCard';
import OrderOriginCard from './cards/OrderOriginCard';
import OrderDestinationCard from './cards/OrderDestinationCard';
import OrderFilesCard from './cards/OrderFilesCard';
import OrderInfoCard from './cards/OrderInfoCard';
import RejectClaimModal from '../modals/RejectClaimModal';
import AcceptClaimModal from '../modals/AcceptClaimModal';
import ClaimNotes from './ClaimNotes';
import AddNoteModal from '../modals/AddNoteModal';
import { addDays } from 'date-fns';
import ConfirmReopenModal from '../modals/ConfirmReopenModal';
import { dateFormatter } from '../formatters';
import { SectionHeader } from '../blocks';
import PreApproveClaimModal from '../modals/PreApproveClaimModal';
import OrderNotes from './OrderNotes';
import Exceptions from './Exceptions';

const Claims = () => {
    const { state, callbacks } = useContext(Context);
    const [tab, setTab] = useState(TABS.DETAILS);
    const navigate = useNavigate();

    let content;

    if (!state.claim || state.loading.claim) {
        content = (
            <Grid
                item
                css={css`
                    height: 100%;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <CircularProgress
                    size={200}
                    css={css`
                        color: ${colors.greens.primary};
                    `}
                />
            </Grid>
        );
    } else {
        content = (
            <>
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-wrap: nowrap;
                        padding: 2rem max(2rem, 10%);
                        justify-content: space-between;
                        padding-bottom: 0;
                    `}
                >
                    <Grid
                        container
                        direction="row"
                        css={css`
                            align-items: center;
                            justify-content: flex-start;
                        `}
                    >
                        <Grid item>
                            <Title>{`Claim ${state.claim?.claim_number}`}</Title>
                        </Grid>
                        {(state.claim?.notes || []).some((note) => !!note.requires_acknowledgement) && (
                            <Grid
                                item
                                css={css`
                                    margin-left: 1rem;
                                `}
                            >
                                <Status color={colors.reds.primary}>Response Requested in Notes</Status>
                            </Grid>
                        )}
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        css={css`
                            align-items: center;
                            justify-content: flex-end;
                        `}
                    >
                        {tab === TABS.CLAIM_NOTES && (
                            <Grid
                                item
                                css={css`
                                    margin-right: 1rem;
                                `}
                            >
                                <PrimaryButton onClick={() => callbacks.setModal(CLAIM_DETAILS_MODALS.ADD_NOTE)}>
                                    Add Note
                                </PrimaryButton>
                            </Grid>
                        )}
                        <Grid item>
                            <Status color={CLAIM_STATUS_COLORS[state.claim?.claim_status]}>
                                {CLAIM_STATUSES[state.claim?.claim_status]}
                            </Status>
                        </Grid>
                    </Grid>
                </Grid>
                {state?.claim?.status === 'INVESTIGATING' && state?.claim?.reopened_at && (
                    <Grid
                        container
                        direction="row"
                        css={css`
                            justify-content: flex-end;
                            padding: 2rem max(2rem, 10%);
                            padding-bottom: 0;
                            padding-top: 0;
                        `}
                    >
                        <Grid item>
                            <SectionHeader>
                                Claim Reopened by {state?.claim?.reopening_user?.username} at{' '}
                                {dateFormatter(state?.claim?.reopened_at)}
                            </SectionHeader>
                        </Grid>
                    </Grid>
                )}
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-grow: 1;
                        flex-wrap: nowrap;
                        padding: 2rem max(2rem, 10%);
                    `}
                >
                    {tab === TABS.DETAILS && (
                        <>
                            <Grid
                                item
                                xs={6}
                                css={css`
                                    padding-right: 1rem;
                                `}
                            >
                                <Grid container direction="column">
                                    <ClaimInfoCard claim={state.claim} />
                                    <ClaimAdjustmentsCard claim={state.claim} />
                                    <ClaimItemsCard claim={state.claim} />
                                    <SupportingDocumentsCard
                                        claim={state.claim}
                                        callbacks={callbacks}
                                        isEditable={state.isEditable}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                css={css`
                                    padding-left: 1rem;
                                `}
                            >
                                <Grid container direction="column">
                                    <OrderOriginCard order={state.claim?.order} claim={state.claim} />
                                    <OrderDestinationCard order={state.claim?.order} claim={state.claim} />
                                    <OrderInfoCard order={state.claim?.order} claim={state.claim} />
                                    <OrderFilesCard
                                        order={state.claim?.order}
                                        attr="delivery_photo"
                                        label="Delivery Photos"
                                    />
                                    <OrderFilesCard order={state.claim?.order} attr="pod" label="Proof of Delivery" />
                                    <OrderFilesCard
                                        order={state.claim?.order}
                                        attr="proof_of_pickup"
                                        label="Proof of Pickup"
                                    />
                                    {(state?.returnOrders || []).map((returnOrder) => (
                                        <>
                                            <OrderFilesCard
                                                order={returnOrder}
                                                attr="delivery_photo"
                                                label={`Return Order${returnOrder?.order_number} Delivery Photos`}
                                            />
                                            <OrderFilesCard
                                                order={returnOrder}
                                                attr="pod"
                                                label={`Return Order ${returnOrder?.order_number} Proof of Delivery`}
                                            />
                                            <OrderFilesCard
                                                order={returnOrder}
                                                attr="proof_of_pickup"
                                                label={`Return Order ${returnOrder?.order_number} Proof of Pickup`}
                                            />
                                        </>
                                    ))}
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {tab === TABS.CLAIM_NOTES && <ClaimNotes claim={state.claim} notes={state.claim?.notes || []} />}
                    {tab === TABS.ORDER_NOTES && <OrderNotes order={state.claim?.order} />}
                    {tab === TABS.EXCEPTIONS && <Exceptions order={state.claim?.order} />}
                </Grid>
            </>
        );
    }

    let actions = [];
    if (state.isEditable) {
        switch (state.claim?.claim_status) {
            case 'SUBMITTED':
                actions.push({
                    loading: state.loading.updateClaim,
                    label: 'Start Investigation',
                    callback: () => callbacks.updateClaim({ claim_status: 'INVESTIGATING' }),
                });
                break;
            case 'INVESTIGATING':
                actions.push({
                    loading: state.loading.updateClaim,
                    label: 'Approve Claim',
                    callback: () => callbacks.setModal(CLAIM_DETAILS_MODALS.ACCEPT_CLAIM),
                });
                actions.push({
                    loading: state.loading.updateClaim,
                    label: 'Pre-Approve Claim',
                    callback: () => callbacks.setModal(CLAIM_DETAILS_MODALS.PRE_APPROVE_CLAIM),
                });
                actions.push({
                    loading: state.loading.updateClaim,
                    label: 'Reject Claim',
                    callback: () => callbacks.setModal(CLAIM_DETAILS_MODALS.REJECT_CLAIM),
                });
                break;
            case 'PRE_APPROVED':
                actions.push({
                    loading: state.loading.updateClaim,
                    label: 'Finalize Approval',
                    callback: () => callbacks.setModal(CLAIM_DETAILS_MODALS.ACCEPT_CLAIM),
                });
                break;
        }
    }

    // Can only re-open claim if it was rejected within the last 30 days
    const canReopenClaim = useMemo(() => {
        if (state.claim?.claim_status !== 'REJECTED') return false;

        const rejectedAt = state.claim?.rejected_at ? new Date(state.claim?.rejected_at) : null;
        if (!rejectedAt) return false;

        const thirtyDaysAfterClaim = addDays(rejectedAt, 30);

        return thirtyDaysAfterClaim > new Date();
    }, [state.claim]);

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={() => callbacks.setNotification({})}
            />
            <EditClaimModal
                open={!!state.editingClaim}
                claim={state.editingClaim}
                onClose={() => callbacks.editClaim(null)}
                onSave={() => {
                    callbacks.editClaim(null);
                    callbacks.refetch();
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <CloseClaimModal
                open={state.modal === CLAIM_DETAILS_MODALS.CLOSE_CLAIM}
                claim={state.claim}
                onClose={() => callbacks.setModal(null)}
                onSave={() => {
                    callbacks.setModal(null);
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <ConfirmReopenModal
                open={state.modal === CLAIM_DETAILS_MODALS.CONFIRM_REOPEN_CLAIM}
                claim={state.claim}
                onClose={() => callbacks.setModal(null)}
                onSave={() => {
                    callbacks.setModal(null);
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <RejectClaimModal
                open={state.modal === CLAIM_DETAILS_MODALS.REJECT_CLAIM}
                claim={state.claim}
                onClose={() => callbacks.setModal(null)}
                onSave={() => {
                    callbacks.setModal(null);
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <AcceptClaimModal
                open={state.modal === CLAIM_DETAILS_MODALS.ACCEPT_CLAIM}
                claim={state.claim}
                onClose={() => callbacks.setModal(null)}
                onSave={() => {
                    callbacks.setModal(null);
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <PreApproveClaimModal
                open={state.modal === CLAIM_DETAILS_MODALS.PRE_APPROVE_CLAIM}
                claim={state.claim}
                onClose={() => callbacks.setModal(null)}
                onSave={() => {
                    callbacks.setModal(null);
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <AddNoteModal
                open={state.modal === CLAIM_DETAILS_MODALS.ADD_NOTE}
                claim={state.claim}
                onClose={() => callbacks.setModal(null)}
                onSave={() => {
                    callbacks.setModal(null);
                    callbacks.refetch();
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <Grid
                container
                direction="row"
                css={css`
                    flex-wrap: nowrap;
                    padding: 0 2rem;
                    border-bottom: 1px solid ${colors.greys.border};
                    background: ${colors.white.primary};
                `}
            >
                <Grid
                    item
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: flex-start;
                    `}
                >
                    <OnwardBreadcrumbInactive onClick={() => navigate('/claims')}>Claims</OnwardBreadcrumbInactive>
                    <OnwardBreadcrumbSpacer />
                    <OnwardBreadcrumbActive>{state.claim?.claim_number}</OnwardBreadcrumbActive>
                </Grid>
                <Grid
                    item
                    css={css`
                        display: flex;
                        flex-grow: 1;
                        align-items: center;
                        justify-content: space-around;
                    `}
                >
                    <OnwardTabContainer value={tab} onChange={(_, tab) => setTab(tab)}>
                        <OnwardTab value={TABS.DETAILS} label="Details" />
                        <OnwardTab value={TABS.CLAIM_NOTES} label="Claim Notes" />
                        <OnwardTab value={TABS.ORDER_NOTES} label="Order Notes" />
                        <OnwardTab value={TABS.EXCEPTIONS} label="Exceptions" />
                    </OnwardTabContainer>
                </Grid>
                <Grid
                    item
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                    `}
                >
                    {canReopenClaim && (
                        <PrimaryButton onClick={() => callbacks.setModal(CLAIM_DETAILS_MODALS.CONFIRM_REOPEN_CLAIM)}>
                            Re-Open Claim
                        </PrimaryButton>
                    )}
                    <Tooltip style={{ color: '#59B863' }} title={'Edit Claim'} placement="top">
                        <IconButton disabled={!state.isEditable} onClick={() => callbacks.editClaim(state.claim)}>
                            <EditIcon
                                css={css`
                                    color: ${state.isEditable ? colors.greens.primary : colors.greys.primary};
                                `}
                            />
                        </IconButton>
                    </Tooltip>
                    <Tooltip style={{ color: '#59B863' }} title={'Close Claim'} placement="top">
                        <IconButton
                            disabled={!state.isEditable}
                            onClick={() => callbacks.setModal(CLAIM_DETAILS_MODALS.CLOSE_CLAIM)}
                        >
                            <CancelIcon
                                css={css`
                                    color: ${state.isEditable ? colors.reds.primary : colors.greys.primary};
                                `}
                            />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid
                item
                css={css`
                    flex-grow: 1;
                    display: contents;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        width: 100%;
                        height: 100%;
                        overflow-y: auto;
                        flex-wrap: nowrap;
                    `}
                >
                    {content}
                </Grid>
            </Grid>
            {actions.length > 0 && (
                <Grid
                    container
                    direction="row"
                    css={css`
                        padding: 1rem 2rem;
                        border-top: 1px solid ${colors.greys.border};
                        background: ${colors.white.primary};
                        justify-content: center;
                    `}
                >
                    {actions.map((action, index) => (
                        <Grid
                            item
                            key={index}
                            css={css`
                                ${index < actions.length - 1 && 'margin-right: 1rem;'}
                                ${index > 0 && 'margin-left: 1rem;'}
                            `}
                        >
                            <PrimaryButton loading={action.loading} onClick={action.callback}>
                                {action.label}
                            </PrimaryButton>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(Claims);
